/* eslint-disable no-underscore-dangle */
import _ from 'underscore';
import { stringifySync } from '@bingads-webui/safe-json';
import { setupQualtrics } from './setup';

let _PreferenceService;
let _AppActivity;

const logSurvey = (message, options) => {
  if (_AppActivity) {
    try {
      if (options != null && options.surveyId) {
        const {
          surveyId,
        } = options;
        _AppActivity.trace(`${surveyId}_${message}`, 'survey-logs');
      } else {
        _AppActivity.trace(message, 'survey-logs');
      }
    } catch (error) {
      console.log(error);
    }
  }
};

const updateQuatricsEvents = (eventName) => {
  window.qualtrics = {
    ...window.qualtrics,
    isLoaded: true,
    events: {
      // we replace all previous events, because we only want 1 event
      // based prompt to show at any given time
      [eventName]: true,
    },
  };
};

const retriggerQSI = () => {
  if (window.QSI && window.QSI.API) {
    // re-run qualtrics to show dynamic popups
    const { API } = window.QSI;
    API.unload();
    API.load();
    API.run();

    logSurvey('QualtricsReInitialized');
    return true;
  }
  logSurvey('QualtricsNotReInitialized');
  return false;
};

export const setQualtricsHasCampaign = () => {
  // don't trigger event if qualtrics isn't setup
  if (!window.qualtrics) {
    logSurvey('QualtricsIsNotInitialized');
    return false;
  }

  if (window.qualtrics.hasCampaign) {
    logSurvey('HasTriggeredHasCampaign');
    return true;
  }

  if (window.QSI && window.QSI.API) {
    window.qualtrics.hasCampaign = true;
    logSurvey('FirstTriggeredHasCampaign');
    return retriggerQSI();
  }
  logSurvey('QSICheckFailedTriggeredHasCampaign');
  return false;
};

export const triggerQualtricsEvent = (eventName) => {
  // don't trigger event if qualtrics isn't setup
  if (!window.qualtrics) { return false; }

  // Always push the event to qualtrics, so they can track how many times
  // it fires.
  window._qsie = window._qsie || [];
  window._qsie.push(eventName);

  // Only update qualtrics if the event wasn't previously set
  if (!window.qualtrics.events[eventName]) {
    updateQuatricsEvents(eventName);

    return retriggerQSI();
  }
  return false;
};

function createDate(date) {
  return date ? new Date(date) : new Date();
}

function getMonthAgo(month) {
  const now = new Date();
  now.setTime(Date.now() - (86400000 * (30 * month)));
  return now;
}

function getSurveyPreference() {
  if (_PreferenceService == null) {
    return {};
  }

  const surveyPreference = _PreferenceService.findByNameAtUserLevel('SurveyPreference');
  if (surveyPreference == null || surveyPreference.Name == null) {
    return ({
      Name: 'SurveyPreference',
      Data: {
      },
      Type: 'string',
      Version: 1,
    });
  }
  return surveyPreference;
}

function getSurveyDataWithSurveyId(surveyId, surveyPre) {
  if (_PreferenceService == null) {
    return {};
  }

  const surveyPreference = surveyPre || getSurveyPreference();
  const surveyData = _.chain(surveyPreference)
    .result('Data')
    .result(surveyId)
    .value();

  if (surveyData == null) {
    return {};
  }
  return surveyData;
}

export function setSurveyShowed(surveyId) {
  try {
    if (_PreferenceService == null) {
      logSurvey('Set_NoPreference', { surveyId });
      return;
    }

    const surveyPreference = getSurveyPreference();
    const surveyData = getSurveyDataWithSurveyId(surveyId, surveyPreference);
    surveyPreference.Data[surveyId] = {
      ...surveyData,
      show: {
        date: new Date().toISOString(),
      },
    };
    logSurvey(stringifySync({
      type: `${surveyId}_SurveyPreference`,
      data: surveyPreference.Data[surveyId],
    }));
    _PreferenceService.setAtUserLevel(surveyPreference);
  } catch (e) {
    console.log(e);
  }
}

function surveyIsNotShowedOrExpired(surveyId, month) {
  if (_PreferenceService == null) {
    logSurvey('Check_NoPreference', { surveyId });
    return false;
  }

  const expiredDate = getMonthAgo(month);
  const surveyData = getSurveyDataWithSurveyId(surveyId);
  const show = _.chain(surveyData)
    .result('show')
    .value();

  logSurvey(stringifySync({
    type: `${surveyId}_ShowDependency2`,
    data: {
      expiredDate,
      show,
    },
  }));
  if (show == null) {
    logSurvey('SurveyNotShowed', { surveyId });
    return true;
  }
  const showedDate = createDate(show.date);
  if (showedDate <= expiredDate) {
    logSurvey('SurveyExpired', { surveyId });
    return true;
  }
  logSurvey('SurveyShowed', { surveyId });
  return false;
}

export function shouldShowSurvey(surveyId) {
  logSurvey('ShouldShowSurveyStart', { surveyId });
  try {
    const {
      CurrentAccountCreatedDate,
      hasCampaign,
    } = window.qualtrics;
    const oneMonthAgo = getMonthAgo(1);
    const accountCreatedDate = createDate(CurrentAccountCreatedDate);

    logSurvey(stringifySync({
      type: `${surveyId}_ShowDependency1`,
      data: {
        CurrentAccountCreatedDate,
        hasCampaign,
        oneMonthAgo,
        accountCreatedDate,
      },
    }));
    switch (surveyId) {
      case 'ESCWebUISurvey':
        if (hasCampaign && accountCreatedDate <= oneMonthAgo) {
          return surveyIsNotShowedOrExpired(surveyId, 6);
        }
        logSurvey('ShouldNotShowWebUISurvey', { surveyId });
        return false;
      case 'WebUISurvey':
        if (accountCreatedDate <= oneMonthAgo) {
          return surveyIsNotShowedOrExpired(surveyId, 6);
        }
        logSurvey('ShouldNotShowWebUISurvey', { surveyId });
        return false;
      default:
        logSurvey('ShouldNotShowSurvey', { surveyId });
        return false;
    }
  } catch (e) {
    console.log(e);
  }
  logSurvey('ShouldShowSurveyEnd', { surveyId });
  return false;
}

export const updateGlobalQualtricsData = (serverContext) => {
  let {
    CurrentUser,
    CurrentCustomer,
    CurrentAccount,
    Identity,
    Omniture,
    CurrentLocale,
  } = serverContext;

  CurrentUser = CurrentUser || {};
  CurrentCustomer = CurrentCustomer || {};
  CurrentAccount = CurrentAccount || {};
  Identity = Identity || {};
  Omniture = Omniture || {};
  CurrentLocale = CurrentLocale || '';

  let { Language } = CurrentUser;
  // if CurrentUser doesn't exist yet (e.g. signup), fallback on CurrentLocale
  if (!Language && CurrentLocale.toLowerCase().indexOf('en') === 0) {
    Language = 'EN';
  }

  window.qualtrics = {
    aid: CurrentAccount.Id,
    cid: CurrentAccount.AdvertiserCustomerID,
    uid: CurrentUser.Id,
    CustomerName: CurrentCustomer.Name,
    AccountMode: CurrentAccount.AccountMode,
    UserName: CurrentUser.Name || Identity.UserName,
    Email: CurrentUser.Email || Identity.Email,
    SessionId: Omniture.SessionId,
    events: {},
    isLoaded: false,
    Language,
    IsInternal: CurrentUser.IsInternal,
    hasCampaign: false,
    CurrentAccountCreatedDate: CurrentAccount.CreatedDate,
    shouldShowSurvey,
  };
};

export function messageHandler(e) {
  if ((e.origin === 'https://microsoft.qualtrics.com' || e.origin === 'https://ui.ads.microsoft.com') && e.data) {
    const {
      surveyId,
      event,
      message,
    } = e.data;
    if (event === 'show') {
      setSurveyShowed(surveyId);
    } else if (event === 'log' && _AppActivity) {
      try {
        _AppActivity.trace(message, 'survey-logs');
      } catch (error) {
        console.log(error);
      }
    }
  }
}

function setupIframeCommunication() {
  window.addEventListener('message', messageHandler);
}

export function setupQualtricsWithData(serverContext, options = {}) {
  const {
    PreferenceService,
    enableIframeCommunication,
    appActivity,
  } = options;
  updateGlobalQualtricsData(serverContext);
  if (enableIframeCommunication) {
    _PreferenceService = PreferenceService;
    _AppActivity = appActivity;
    setupIframeCommunication();
    // log all the information at start-up;
    logSurvey(stringifySync(window.qualtrics));
  }
  setupQualtrics();
}
